@let languages = languageStore.languages();
@let prefix = textType();

@if (isLoading()) {
  <div class="pt-32">
    <aw-loading-animation></aw-loading-animation>
  </div>
}
@if (texts() && !isLoading() && languages && !languageStore.isLoading()) {
  <aw-section [testIdAffix]="prefix + '-allTexts'">
    <aw-section-header>
      <aw-section-headline>
        {{ headline() }}
      </aw-section-headline>
      <aw-section-header-actions>
        <input
          #fileInput
          hidden
          type="file"
          accept="text/csv"
          (change)="onFileSelected($event)" />
        <aw-button
          [id]="prefix + '-save-texts'"
          [type]="'submit'"
          [isDisabled]="!hasChanges"
          (click)="save()"
          [text]="cmsTextProvider.shared.save"
          [style]="'primary'" />
        <aw-button
          [id]="prefix + '-cancel-texts'"
          [type]="'button'"
          [isDisabled]="!hasChanges"
          (click)="reload()"
          [text]="cmsTextProvider.shared.cancel"
          [style]="'primary-alt'" />
      </aw-section-header-actions>
    </aw-section-header>
    <div class="flex flex-col gap-4">
      <div
        [formGroup]="filterForm"
        class="mb-4"
        [attr.data-testid]="prefix + '-texts' | appendTestId: 'filter'">
        <aw-text-input
          id="search-filter"
          [placeholder]="cmsTextProvider.allTexts.searchPlaceholder"
          formControlName="search"></aw-text-input>
      </div>
      <div
        class="flex items-center gap-4"
        [attr.data-testid]="prefix + '-texts' | appendTestId: 'filter'">
        <div class="flex w-full items-center gap-4">
          {{ cmsTextProvider.shared.shownTranslations }}
          @for (language of languages; track $index) {
            @if (isActiveLanguages(language)) {
              <aw-toggle-chip
                [isChecked]="true"
                [attr.data-testid]="language.prefix + '-chip'"
                [attr.aria-label]="prefix + '-chip-' + language.prefix"
                [text]="language.name"
                (click)="toggleLanguage(language)">
                <span class="material-symbols-rounded"> check </span>
                @let count = getChangedCountForLang(language);
                @if (count > 0) {
                  <span
                    class="absolute -top-3 -right-3 rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-red-100">
                    {{ count }}
                  </span>
                }
              </aw-toggle-chip>
            } @else {
              <aw-toggle-chip
                [isChecked]="false"
                [attr.data-testid]="prefix + '-' + language.prefix + '-chip'"
                [attr.aria-label]="prefix + '-chip-' + language.prefix"
                [text]="language.name"
                (click)="toggleLanguage(language)">
                @let count = getChangedCountForLang(language);
                @if (count > 0) {
                  <span
                    class="absolute -top-3 -right-3 rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-red-100">
                    {{ count }}
                  </span>
                }
              </aw-toggle-chip>
            }
          }
        </div>
        <div class="flex w-1/3 justify-end gap-2">
          <aw-button
            [id]="'import-texts'"
            [text]="cmsTextProvider.allTexts.import"
            [type]="'button'"
            [style]="'primary-alt'"
            (click)="fileInput.click()" />
          <aw-button
            [id]="'export-texts'"
            [text]="cmsTextProvider.allTexts.export"
            [style]="'primary-alt'"
            [type]="'button'"
            (click)="exportTexts()" />
        </div>
      </div>
      <aw-table
        [isLoading]="isLoading()"
        [hiddenColumns]="hiddenColumns()"
        id="ticket-table"
        [data]="filteredTexts"
        [header]="header"
        [columns]="columns"
        [striped]="true"
        (pageChange)="filterForm.updateValueAndValidity()">
        <ng-template #header let-sortBy="sortBy">
          <aw-table-header
            cellName="entityType"
            [sortParams]="{
              attribute: 'entityType',
              compare: textEntityTypeFilter,
            }"
            class="w-full overflow-hidden"
            [attr.data-testid]="
              prefix + '-texts' | appendTestId: 'header-entityType'
            "
            (onSort)="sortBy($event)">
            {{ cmsTextProvider.allTexts.source }}
          </aw-table-header>
          <aw-table-header
            cellName="relationType"
            [sortParams]="{
              attribute: 'relationType',
              compare: textRelationTypeFilter,
            }"
            class="w-full overflow-hidden"
            [attr.data-testid]="
              prefix + '-texts' | appendTestId: 'header-relationType'
            "
            (onSort)="sortBy($event)">
            {{ cmsTextProvider.allTexts.relationType }}
          </aw-table-header>
          @for (language of languages; track $index) {
            <aw-table-header
              [cellName]="language.name"
              [sortParams]="{
                attribute: language.name,
                compare: textValueFilter,
                language: language.prefix,
              }"
              class="w-full overflow-hidden"
              [attr.data-testid]="
                prefix + '-texts'
                  | appendTestId
                    : 'header-value-' + language.name.toLocaleLowerCase()
              "
              (onSort)="sortBy($event)">
              {{ language.name }}
            </aw-table-header>
          }
          <aw-table-header
            cellName="lastEdited"
            [sortParams]="{
              attribute: 'lastEditedAt',
              compare: dateLastEditedAtFilter,
            }"
            class="w-full overflow-hidden"
            [attr.data-testid]="
              prefix + '-texts' | appendTestId: 'header-lastEditedAt'
            "
            (onSort)="sortBy($event)">
            {{ cmsTextProvider.shared.lastEditedAt }}
          </aw-table-header>
        </ng-template>
        <ng-template #columns let-data="data">
          @let entityLink =
            data.entityType | toEntityLink: data.id ?? data.relationType;
          <aw-table-cell cellName="entityType">
            <a
              class="min-h-12 w-full overflow-hidden text-ellipsis whitespace-nowrap"
              [ngClass]="{
                'text-primary-main cursor-pointer hover:underline': entityLink,
              }"
              [href]="entityLink"
              [attr.data-testid]="
                prefix + '-texts' | appendTestId: 'entityType-' + data.id
              ">
              {{ data.entityType | toEntityName }}
            </a>
          </aw-table-cell>
          <aw-table-cell
            cellName="relationType"
            class="w-full overflow-hidden"
            [attr.data-testid]="
              prefix + '-texts' | appendTestId: 'relationType-' + data.id
            ">
            {{ data.relationType }}
          </aw-table-cell>
          @for (language of languages; track $index) {
            @if (isEditing(data, language)) {
              <aw-table-cell
                [cellName]="language.name"
                class="bg-standard-select relative w-full px-3 py-1"
                [attr.data-testid]="
                  prefix + '-texts' | appendTestId: 'value-' + data.id
                "
                (click)="startEdit(data, language)">
                <div class="flex w-full flex-col">
                  <div class="relative px-3 py-1">
                    <textarea
                      autofocus
                      type="text"
                      class="min-h-content field-sizing-content h-auto w-full rounded-md"
                      [attr.data-testid]="
                        prefix + '-texts' | appendTestId: 'value-' + data.id
                      "
                      [ngModel]="getLanguageValue(data, language)"
                      (ngModelChange)="onValueChange($event, data, language)"
                      (keydown.enter)="stopEdit()"></textarea>
                    <span
                      class="material-symbols-rounded absolute top-3 right-12 cursor-pointer"
                      (click)="stopEdit()">
                      check
                    </span>
                    <span
                      class="material-symbols-rounded absolute top-3 right-5 cursor-pointer"
                      (click)="
                        $event.preventDefault(); stopEditAndReset(language)
                      ">
                      close
                    </span>
                  </div>
                  @if (isTemplateText(data.entityType, data.relationType)) {
                    @let attributes =
                      getTemplateText(data.entityType, data.relationType);
                    {{ cmsTextProvider.shared.availableAttributes }}
                    <div class="pt-1">
                      @for (attribute of attributes; track $index) {
                        - {{ '{{' + attribute + '}}' }}<br />
                      }
                    </div>
                  }
                </div>
              </aw-table-cell>
            }
            @if (!isEditing(data, language)) {
              <aw-table-cell
                [cellName]="language.name"
                class="group relative flex w-full max-w-full justify-between overflow-hidden p-3 hover:bg-[#E4EEF8]"
                [style.font-weight]="
                  getLanguageIsChanged(data, language) ? 'bold' : 'normal'
                "
                [attr.data-testid]="
                  prefix + '-texts' | appendTestId: 'value-' + data.id
                ">
                {{ getLanguageValue(data, language) }}
                <span
                  class="material-symbols-rounded absolute right-0 cursor-pointer bg-inherit opacity-0 transition-opacity group-hover:opacity-100"
                  (click)="startEdit(data, language)">
                  edit
                </span>
              </aw-table-cell>
            }
          }
          <aw-table-cell
            cellName="lastEdited"
            class="w-full p-3"
            [attr.data-testid]="
              prefix + '-texts' | appendTestId: 'lastEditedAt-' + data.id
            ">
            {{ data.text.lastEditedAt | date: DATE_FORMAT_LONG }}
          </aw-table-cell>
        </ng-template>
      </aw-table>
    </div>
  </aw-section>
}
